/**
 * DebugOverlay — renders face landmark dots and IPD line for debugging.
 *
 * When enabled, draws all 478 MediaPipe landmarks as green dots and a cyan
 * line between the pupils. This helps verify that tracking coordinates align
 * correctly with the camera feed and that the scale/positioning pipeline works.
 *
 * Activate with `<forge-tryon debug>` or `config.debug = true`.
 */

import type { EyewearLandmarks as FaceLandmarks } from '@mirrably/tracking';
import { VIDEO_PLANE_DEPTH, LM_TO_SCENE_SCALE } from '@mirrably/renderer';

/** Total landmarks from MediaPipe Face Landmarker. */
const LANDMARK_COUNT = 478;

const LM_TO_SCENE = LM_TO_SCENE_SCALE;

export class DebugOverlay {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private points: any | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private positionAttr: any | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private ipdLine: any | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private ipdPositionAttr: any | null = null;

  /**
   * Create the debug geometry and add it to the scene.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async init(scene: any): Promise<void> {
    const {
      BufferGeometry,
      BufferAttribute,
      Points,
      PointsMaterial,
      Line,
      LineBasicMaterial,
    } = await import('three');

    // ── 478 landmark dots ──────────────────────────────────────────────────
    const positions = new Float32Array(LANDMARK_COUNT * 3);
    const geometry = new BufferGeometry();
    const posAttr = new BufferAttribute(positions, 3);
    posAttr.setUsage(35048); // DynamicDrawUsage
    geometry.setAttribute('position', posAttr);

    const material = new PointsMaterial({
      color: 0x00ff66,
      size: 3,
      sizeAttenuation: false,
      depthTest: false,
    });

    this.points = new Points(geometry, material);
    this.points.renderOrder = 10; // draw on top of everything
    this.positionAttr = posAttr;
    scene.add(this.points);

    // ── IPD line (cyan) ────────────────────────────────────────────────────
    const linePositions = new Float32Array(2 * 3); // 2 vertices
    const lineGeom = new BufferGeometry();
    const lineAttr = new BufferAttribute(linePositions, 3);
    lineAttr.setUsage(35048);
    lineGeom.setAttribute('position', lineAttr);

    const lineMat = new LineBasicMaterial({
      color: 0x00ffff,
      depthTest: false,
    });

    this.ipdLine = new Line(lineGeom, lineMat);
    this.ipdLine.renderOrder = 10;
    this.ipdPositionAttr = lineAttr;
    scene.add(this.ipdLine);
  }

  /**
   * Update landmark positions from the latest tracking data.
   */
  update(landmarks: FaceLandmarks): void {
    // Update 478-point dots
    if (this.positionAttr && landmarks.rawLandmarks) {
      const raw = landmarks.rawLandmarks;
      for (let i = 0; i < Math.min(raw.length, LANDMARK_COUNT); i++) {
        const p = raw[i]!;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.positionAttr.setXYZ(
          i,
          p[0] * LM_TO_SCENE,
          p[1] * LM_TO_SCENE,
          -VIDEO_PLANE_DEPTH,
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.positionAttr.needsUpdate = true;
    }

    // Update IPD line
    if (this.ipdPositionAttr) {
      const lp = landmarks.leftPupil;
      const rp = landmarks.rightPupil;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.ipdPositionAttr.setXYZ(0, lp[0] * LM_TO_SCENE, lp[1] * LM_TO_SCENE, -VIDEO_PLANE_DEPTH);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.ipdPositionAttr.setXYZ(1, rp[0] * LM_TO_SCENE, rp[1] * LM_TO_SCENE, -VIDEO_PLANE_DEPTH);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.ipdPositionAttr.needsUpdate = true;
    }
  }

  setVisible(visible: boolean): void {
    if (this.points) this.points.visible = visible;
    if (this.ipdLine) this.ipdLine.visible = visible;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  destroy(scene?: any): void {
    if (this.points) {
      scene?.remove(this.points);
      this.points.geometry?.dispose();
      this.points.material?.dispose();
      this.points = null;
    }
    if (this.ipdLine) {
      scene?.remove(this.ipdLine);
      this.ipdLine.geometry?.dispose();
      this.ipdLine.material?.dispose();
      this.ipdLine = null;
    }
    this.positionAttr = null;
    this.ipdPositionAttr = null;
  }
}
